import {
  DEFAULT_DESIGN,
  DESIGN_ATTRIBUTE,
  DESIGN_STORAGE_KEY,
  type DesignId,
  isDesignId,
} from '../config';

/**
 * The active design lives in one place: the `data-design` attribute on <html>. The inline
 * script (DesignScript) sets it before first paint; `setDesign` changes it; React reads it
 * through `useDesign` (useSyncExternalStore), so CSS and components can never disagree.
 */
export function readDesign(): DesignId {
  const value = document.documentElement.getAttribute(DESIGN_ATTRIBUTE);
  return isDesignId(value) ? value : DEFAULT_DESIGN;
}

export function subscribeDesign(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [DESIGN_ATTRIBUTE],
  });

  // Keep other tabs in step
  const onStorage = (event: StorageEvent) => {
    if (event.key === DESIGN_STORAGE_KEY && isDesignId(event.newValue)) {
      document.documentElement.setAttribute(DESIGN_ATTRIBUTE, event.newValue);
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    observer.disconnect();
    window.removeEventListener('storage', onStorage);
  };
}

/** Marks <html> while a design switch is animating (see globals.css) */
const TRANSITION_ATTRIBUTE = 'data-design-transition';

/**
 * Switches the design. Where the browser supports view transitions, the new design is revealed
 * in a circle growing from `origin` (the switch button), or cross-fades without one.
 */
export function setDesign(id: DesignId, origin?: { x: number; y: number }) {
  const root = document.documentElement;
  if (root.getAttribute(DESIGN_ATTRIBUTE) === id) return;

  const commit = () => {
    root.setAttribute(DESIGN_ATTRIBUTE, id);
    try {
      localStorage.setItem(DESIGN_STORAGE_KEY, id);
    } catch {
      // private mode: the choice just isn't remembered
    }
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || typeof document.startViewTransition !== 'function') {
    commit();
    return;
  }

  if (!origin) {
    document.startViewTransition(commit);
    return;
  }

  root.setAttribute(TRANSITION_ATTRIBUTE, '');
  const transition = document.startViewTransition(commit);
  transition.ready
    .then(() => {
      const { x, y } = origin;
      const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
      root.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        {
          duration: 800,
          easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
          pseudoElement: '::view-transition-new(root)',
        },
      );
    })
    .catch(() => undefined);
  transition.finished.finally(() => root.removeAttribute(TRANSITION_ATTRIBUTE));
}
